import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import classNames from 'classnames';

import './FastModeHistory.scss';
import Icon from 'src/components/Icon/Icon';
import { RootState } from 'src/configs/store';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import BallsContainer from 'src/components/Betslip/NumberBet/Components/BallsContainer/BallsContainer';
import UseActiveElement from 'src/hooks/useActiveElement';

interface FastModeLottoProps {
  smartInputSelect: Function,
  changeLastValues: Function
}

function FastModeLotto({ smartInputSelect, changeLastValues }: FastModeLottoProps) {
  const { t } = useTranslation();
  const [showHistory, setShowHistory] = useState(true);
  const [showMore, setShowMore] = useState(false);
  const fastInputHistory = useSelector((state: RootState) => state.lotto.fastInputHistory);
  const selectedTimezone = useSelector((state: RootState) => state.odds.selectedTimezone);
  const dispatch = useDispatch();
  const focusedElement = UseActiveElement();

  const handleShowHistory = (e) => {
    e.preventDefault();
    setShowHistory(!showHistory);
  };

  const handleLastValues = (selectedNumbers) => {
    const newNumbers = _.map(selectedNumbers, (selected) => {
      return String(selected.number).padStart(2, '0');
    });
    changeLastValues(newNumbers);
  };

  const handleBoth = (selectedNumbers, marketName) => {
    smartInputSelect(marketName);
    handleLastValues(selectedNumbers);
  };

  const handleShowMore = useCallback(() => {
    setShowMore(!showMore);
  }, [showMore]);

  const tabFunction = useCallback((e) => {

    if (e.key === 'Tab' && focusedElement.className.includes('focusable')){
      e.preventDefault();
      const buttons = document.getElementsByClassName('focus-button');
      (buttons[0] as HTMLElement)?.focus();
    }

    if (e.key === 'Tab' && focusedElement.className.includes('focus-button')){
      e.preventDefault();
      const inputs = document.getElementsByClassName('focusable');
      (inputs[inputs.length -1] as HTMLElement)?.focus();
    }

    if ((e.key === 'ArrowRight')){
      e.preventDefault();
      const inputs = document.getElementsByClassName('focus-button');
      const selectedIndex = _.findIndex(inputs, (input) => input === focusedElement);
      if (selectedIndex >= 0){
        if (inputs[selectedIndex +1]){
          (inputs[selectedIndex +1] as HTMLElement)?.focus();
        } else {
          (inputs[0] as HTMLElement)?.focus();
        }
      }
    }

    if (e.key === 'ArrowLeft'){
      e.preventDefault();
      const inputs = document.getElementsByClassName('focus-button');
      const selectedIndex = _.findIndex(inputs, (input) => input === focusedElement);

      if (selectedIndex >= 0){
        if (inputs[selectedIndex -1]){
          (inputs[selectedIndex -1] as HTMLElement)?.focus();
        } else {
          (inputs[inputs.length -1] as HTMLElement)?.focus();
        }
      }
    }
  }, [ focusedElement]);

  useEffect(() => {
    addEventListener('keydown', tabFunction);
    return () => {
      removeEventListener('keydown', tabFunction);
    };
  }, [fastInputHistory, focusedElement]);


  const handleMarket = (market) => {
    smartInputSelect(market);
    const inputs = document.getElementsByClassName('focusable');
    (inputs[inputs.length -1] as HTMLElement)?.focus();
  };

  const handleNumbers = useCallback((numbers) => {
    handleLastValues(numbers);
    const inputs = document.getElementsByClassName('focusable');
    (inputs[inputs.length -1] as HTMLElement)?.focus();
  }, []);

  const handleBothFunctions = (numbers, market) => {
    handleBoth(numbers, market);
    const inputs = document.getElementsByClassName('focusable');
    (inputs[inputs.length -1] as HTMLElement)?.focus();
  };

  const resetHistory = () => {
    dispatch.lotto.resetLottoFastModeHistory();
  };

  return (
    <div className="fastmode-history">
      <button
        type="button"
        onClick={ handleShowHistory }
        className="fastmode-history__show-history"
      >{ t('fastmode.history') }
        <Icon
          className={
            classNames({
              'fastmode-history__icon': true,
              'fastmode-history__icon--up': showHistory,
              'fastmode-history__icon--down': !showHistory
            })
          }
          icon="dropdownArrow"
        />
      </button>
      <button
        className="fast-mode__clear fast-mode__clear--history"
        onClick={ resetHistory }
        disabled={ _.isEmpty(fastInputHistory) }
      > { t('numbers.clearHistory') }</button>
      <div className="fastmode-history__container">
        {
          fastInputHistory.length > 0 && showHistory && (
            _.map(fastInputHistory, (history, index) => {

              const historyDate = format(history.datetime && utcToZonedTime(history.datetime, selectedTimezone), 'HH:mm:ss');
              if (index < 10 || showMore){
                return (
                  <div className="fastmode-history__history" key={ index }>
                    <span className="fastmode-history__date">
                      { historyDate }
                    </span>
                    <span className="fastmode-history__market-name">
                      { history.marketName }
                    </span>
                    <button
                      type="button"
                      className="fastmode-history__reuse-button focus-button"
                      onClick={ () => handleMarket(history.marketName) }
                    >
                      <Icon className="fastmode-history__reuse" icon="reuse" />
                    </button>
                    <div className="fastmode-history__balls-container">
                      <BallsContainer anyNumbers={ history.selectedNumbers } sameBallset={ history.sameBallset } />
                    </div>
                    <button
                      type="button"
                      className="fastmode-history__reuse-button focus-button"
                      onClick={ () => handleNumbers(history.selectedNumbers) }
                    >
                      <Icon className="fastmode-history__reuse" icon="reuse" />
                    </button>
                    <button
                      type="button"
                      onClick={ () => handleBothFunctions(history.selectedNumbers, history.marketName) }
                      className="fastmode-history__reuse-button fastmode-history__reuse-button--text focus-button"
                    >
                      <Icon className="fastmode-history__reuse" icon="reuse" />
                      <span className="fastmode-history__text-button">{ t('fastmode.both') }</span>
                    </button>
                  </div>
                );
              } else {
                return null;
              }
            })
          )
        }
      </div>
      {
        fastInputHistory.length > 10 &&
        <button
          onClick={ handleShowMore }
          className="fastmode-history__show-more"
          type="button"
        > { !showMore ? t('fastmode.showMore') : t('fastmode.showLess') } </button>
      }
    </div>
  );
}

export default FastModeLotto;
